import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SignatureForm } from '../../utils/constant';
import { editSignature, addSignature, changeIsDefaultSignature, deleteSignature, getSignatures } from './utils';
import { signatureInitialState as initialState } from './constants';
import { isValidZipCode } from 'src/utils/utility-functions';
import parsePhoneNumberFromString from 'libphonenumber-js';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const signatureSlice = createSlice({
  name: 'signature',
  initialState,
  reducers: {
    updateFormField(
      state,
      action: PayloadAction<{
        fieldName: keyof SignatureForm;
        value: string;
      }>,
    ) {
      const { fieldName, value } = action.payload;

      state.formData[fieldName as Exclude<keyof SignatureForm, 'isDefault'>] = value;
    },
    handleSignatureSubmit(state) {
      state.formDataErrors = initialState.formDataErrors;
      state.requestSuccess = initialState.requestSuccess;
      state.requestError = initialState.requestError;

      const formData = state.formData;

      const formattedPhone = parsePhoneNumberFromString(formData.phone, 'US');

      if (!formData.fullName) {
        state.formDataErrors = {
          ...state.formDataErrors,
          fullNameError: 'Full Name is required',
        };
      }

      if (!formData.addressOne) {
        state.formDataErrors = {
          ...state.formDataErrors,
          address1SignatureError: 'Address Line 1 is required',
        };
      }

      if (!formData.city) {
        state.formDataErrors = {
          ...state.formDataErrors,
          citySignatureError: 'City is required',
        };
      }

      if (!formData.state) {
        state.formDataErrors = {
          ...state.formDataErrors,
          stateSignatureError: 'State is required',
        };
      }

      if (!formData.zipCode) {
        state.formDataErrors = {
          ...state.formDataErrors,
          zipSignatureError: 'Zip Code is required',
        };
      }

      if (
        formData.country === 'United States' &&
        formData.state &&
        formData.zipCode &&
        !isValidZipCode(formData.state, formData.zipCode)
      ) {
        state.formDataErrors = {
          ...state.formDataErrors,
          zipSignatureError: 'Zip Code is invalid',
        };
      }

      if (!formData.phone) {
        state.formDataErrors = {
          ...state.formDataErrors,
          phoneError: 'Phone Number is required',
        };
      }

      if (formData.phone && !formattedPhone?.isValid()) {
        state.formDataErrors = {
          ...state.formDataErrors,
          phoneError: 'Wrong phone format',
        };
      }

      if (!formData.country) {
        state.formDataErrors = {
          ...state.formDataErrors,
          countryError: 'Country is required',
        };
      }

      if (formData.email && !emailRegex.test(formData.email)) {
        state.formDataErrors = {
          ...state.formDataErrors,
          emailError: 'Wrong format',
        };
      }

      if (!formData.clientRole) {
        state.formDataErrors = {
          ...state.formDataErrors,
          clientRoleError: 'Client Role is required',
        };
      }
    },
    loadData(state, action) {
      state.formData = action.payload;
    },
    removeSuccessMsg(state) {
      state.requestSuccess = '';
    },
    clearErrors(state) {
      state.requestError = '';
      state.formDataErrors = {
        fullNameError: '',
        barNumberError: '',
        firmNameSignatureError: '',
        address1SignatureError: '',
        address2SignatureError: '',
        citySignatureError: '',
        stateSignatureError: '',
        zipSignatureError: '',
        phoneError: '',
        emailError: '',
        faxError: '',
        countryError: '',
        clientRoleError: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editSignature.fulfilled, (state, action) => {
        state.formData = action.payload;
        state.requestSuccess = 'Successfully updated signature!';
      })
      .addCase(editSignature.rejected, (state, action) => {
        state.requestError = action.payload ? (action.payload as string) : 'Something went wrong!';
      })
      .addCase(addSignature.fulfilled, (state, action) => {
        state.formData = action.payload;
        state.requestSuccess = 'Successfully added signature!';
      })
      .addCase(addSignature.rejected, (state, action) => {
        state.requestError = action.payload ? (action.payload as string) : 'Something went wrong!';
      })
      .addCase(changeIsDefaultSignature.pending, (state) => {
        state.isSettingDefault = true;
      })
      .addCase(changeIsDefaultSignature.fulfilled, (state, action) => {
        state.formData = action.payload;
        state.isSettingDefault = false;
        state.requestSuccess = 'Successfully updated signature!';
      })
      .addCase(changeIsDefaultSignature.rejected, (state, action) => {
        state.isSettingDefault = false;
        state.requestError = action.payload ? (action.payload as string) : 'Something went wrong!';
      })
      .addCase(deleteSignature.fulfilled, (state, action) => {
        state.formData = action.payload;
        state.requestSuccess = 'Successfully deleted signature!';
      })
      .addCase(deleteSignature.rejected, (state, action) => {
        state.requestError = action.payload ? (action.payload as string) : 'Something went wrong!';
      })
      .addCase(getSignatures.pending, (state) => {
        state.isFetchingSignatures = true;
      })
      .addCase(getSignatures.fulfilled, (state) => {
        state.isFetchingSignatures = false;
      })
      .addCase(getSignatures.rejected, (state) => {
        state.isFetchingSignatures = false;
      });
  },
});

export const { updateFormField, handleSignatureSubmit, loadData, removeSuccessMsg, clearErrors } =
  signatureSlice.actions;

export default signatureSlice.reducer;
